import React, { useEffect, useState } from 'react';
import { Paperclip, Plus, Trash2, Eye } from 'lucide-react';
import { licenseService } from '../../services/api';
import FileViewerModal from './FileViewerModal';
import { CHECKLIST_TIPOS, normalizeChecklistTipo } from '../../utils/checklistTipo';

function newLocalKey() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function checklistFromApi(item) {
  return {
    localKey: item.id,
    id: item.id,
    descricao: item.descricao || '',
    tipo_checklist: normalizeChecklistTipo(item.tipo_checklist),
    obrigatorio: Boolean(item.obrigatorio),
    cumprido: Boolean(item.cumprido),
    ordem: item.ordem,
    pendingFiles: [],
    _deleted: false,
  };
}

export async function syncLicenseChecklist(licencaId, draft) {
  if (!licencaId) return;
  for (const item of draft || []) {
    if (item._deleted && item.id) {
      await licenseService.deleteLicencaChecklistItem(item.id);
      continue;
    }
    if (item._deleted) continue;
    let itemId = item.id;
    if (!itemId) {
      const created = await licenseService.createLicencaChecklistItem(licencaId, {
        descricao: item.descricao,
        tipo_checklist: normalizeChecklistTipo(item.tipo_checklist),
        obrigatorio: item.obrigatorio,
        cumprido: item.cumprido,
      });
      itemId = created.data?.id || created.id;
    } else {
      await licenseService.updateLicencaChecklistItem(itemId, {
        descricao: item.descricao,
        tipo_checklist: normalizeChecklistTipo(item.tipo_checklist),
        obrigatorio: item.obrigatorio,
        cumprido: item.cumprido,
      });
    }
    if (itemId && item.pendingFiles?.length) {
      for (const file of item.pendingFiles) {
        await licenseService.uploadLicencaChecklistAnexo(itemId, file);
      }
    }
  }
}

function Anexos({ itemId, pendingFiles, onAddPending, onRemovePending }) {
  const [anexos, setAnexos] = useState([]);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    if (!itemId) return undefined;
    let cancelled = false;
    licenseService
      .listLicencaChecklistAnexos(itemId)
      .then((r) => {
        if (!cancelled) setAnexos(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setAnexos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  return (
    <div className="mt-2 space-y-2">
      <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-700">
        <Paperclip className="h-3.5 w-3.5" />
        Anexar evidência
        <input
          type="file"
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            if (files.length) onAddPending(files);
          }}
        />
      </label>
      {anexos.map((anexo) => (
        <button
          key={anexo.id}
          type="button"
          className="flex items-center gap-1 text-xs text-primary-700 hover:underline"
          onClick={() => setViewer(anexo)}
        >
          <Eye className="h-3.5 w-3.5" />
          {anexo.filename_original}
        </button>
      ))}
      {(pendingFiles || []).map((file, index) => (
        <div key={`${file.name}-${index}`} className="flex items-center justify-between text-xs text-gray-600">
          <span>{file.name} (pendente)</span>
          <button type="button" onClick={() => onRemovePending(index)} className="text-red-600">
            remover
          </button>
        </div>
      ))}
      {viewer && (
        <FileViewerModal
          anexo={viewer}
          loadBlob={() => licenseService.getLicencaChecklistAnexoBlob(viewer.id)}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}

export default function ChecklistEditor({ value, onChange, licencaId }) {
  const items = value || [];

  const update = (index, patch) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const add = () => {
    onChange([
      ...items,
      {
        localKey: newLocalKey(),
        descricao: '',
        tipo_checklist: 'basico',
        obrigatorio: true,
        cumprido: false,
        pendingFiles: [],
      },
    ]);
  };

  return (
    <div className="space-y-3">
      {items.filter((item) => !item._deleted).length === 0 && (
        <p className="text-sm text-gray-500">Nenhum item de checklist. Adicione ou selecione um tipo de licença com modelo.</p>
      )}
      {items.map((item, index) => {
        if (item._deleted) return null;
        return (
          <div key={item.localKey || item.id || index} className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-start gap-2">
              <textarea
                className="form-input min-h-[72px] flex-1"
                value={item.descricao}
                onChange={(e) => update(index, { descricao: e.target.value })}
                placeholder="Descrição do item"
              />
              <button
                type="button"
                className="text-red-600"
                onClick={() => update(index, { _deleted: true })}
                aria-label="Remover item"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                Tipo
                <select
                  className="form-input w-auto"
                  value={normalizeChecklistTipo(item.tipo_checklist)}
                  onChange={(e) => update(index, { tipo_checklist: e.target.value })}
                >
                  {CHECKLIST_TIPOS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(item.obrigatorio)}
                  onChange={(e) => update(index, { obrigatorio: e.target.checked })}
                />
                Obrigatório
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(item.cumprido)}
                  onChange={(e) => update(index, { cumprido: e.target.checked })}
                />
                Cumprido
              </label>
            </div>
            <Anexos
              itemId={item.id}
              pendingFiles={item.pendingFiles}
              onAddPending={(files) => update(index, { pendingFiles: [...(item.pendingFiles || []), ...files] })}
              onRemovePending={(i) =>
                update(index, { pendingFiles: (item.pendingFiles || []).filter((_, idx) => idx !== i) })
              }
            />
          </div>
        );
      })}
      <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-1" onClick={add}>
        <Plus className="h-4 w-4" />
        Novo item
      </button>
    </div>
  );
}
